/**
 * Calendar dates for the admin console: list URL date params (DNKADM-0430) and the ONE
 * place "today", date windows and day arithmetic are computed (DNKADM-0439).
 *
 * The toolbar's From/To inputs write `YYYY-MM-DD` strings into the URL. A hand-edited or
 * stale link can carry anything, and an endpoint that binds a `LocalDate` answers a bad
 * one with a 400, which would leave the list in its error state. Read every date param
 * through {@link parseDateParam} so a bad value reads as "no date" instead.
 *
 * **The calendar rule (DNKADM-0439, pair of DNKAPI-0642).** A day a person sees ("today",
 * a range, Ends/Renews) is a day on the LOCATION's clock, falling back to the tenant's
 * zone, then the device ({@link resolveCalendarZone}; `null` means "the device"). Never
 * `toISOString().slice(0, 10)` (that is the UTC day, tomorrow for a US operator every
 * evening) and never a bare `new Date()` read as a date. A `YYYY-MM-DD` the API sends is a
 * calendar day: it is displayed from its digits ({@link formatApiDate}) and moved with
 * {@link addDays}, never parsed at UTC midnight and reformatted. An Ends/expiration date
 * is the LAST valid day, as DNKAPI-0642's `lastValidDay` returns it: shown as sent, never
 * shifted by a day here. The ESLint guard in `eslint.config.js` keeps new code on these
 * helpers.
 */
import { shortDateLabel, wallClockToIso } from '@/utils/clockTime';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A `YYYY-MM-DD` URL date, or `''` when the value is missing, malformed, or not a real
 * calendar day (`2026-02-30`, `2026-13-01`).
 */
export function parseDateParam(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return '';
  const match = ISO_DATE.exec(raw);
  if (match === null) return '';
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  const real =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return real ? raw : '';
}

// --- Calendar dates on the location clock (DNKADM-0439) ----------------------------------

const pad = (n: number, width = 2) => String(n).padStart(width, '0');
const ymd = (year: number, month: number, day: number) => `${pad(year, 4)}-${pad(month)}-${pad(day)}`;

/** UTC midnight of a calendar day as ms (`setUTCFullYear`, so years 0-99 are not read as 19xx). */
function utcDayMs(year: number, month: number, day: number): number {
  const d = new Date(0);
  d.setUTCFullYear(year, month - 1, day);
  return d.getTime();
}

/** Per-zone day formatter cache; `null` records a zone the runtime rejects. */
const zoneDayFormatters = new Map<string, Intl.DateTimeFormat | null>();

function zoneDayFormatter(zone: string): Intl.DateTimeFormat | null {
  const cached = zoneDayFormatters.get(zone);
  if (cached !== undefined) return cached;
  let formatter: Intl.DateTimeFormat | null;
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    formatter = null;
  }
  zoneDayFormatters.set(zone, formatter);
  return formatter;
}

/** True when `zone` is a non-empty IANA id this runtime can format in. */
export function isUsableZone(zone: string | null | undefined): zone is string {
  return typeof zone === 'string' && zone.trim() !== '' && zoneDayFormatter(zone) !== null;
}

/**
 * The calendar zone: the location's, else the tenant's, else `null` (the device). An
 * unusable id is skipped, never thrown on.
 */
export function resolveCalendarZone(
  locationZone: string | null | undefined,
  tenantZone: string | null | undefined,
): string | null {
  if (isUsableZone(locationZone)) return locationZone;
  if (isUsableZone(tenantZone)) return tenantZone;
  return null;
}

/**
 * The device's IANA zone, read ONCE by a caller that has no location or tenant zone
 * (DNKADM-0442 U4). The day-start helpers take a resolved zone and never read the device
 * implicitly, so what a call computes is visible at its call site and testable with a
 * named zone. Falls back to `UTC` only if the runtime cannot name its own zone.
 */
export function deviceZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (isUsableZone(zone)) return zone;
  } catch {
    /* fall through */
  }
  return 'UTC';
}

/** `zone` when this runtime can format in it, else the device's zone ({@link deviceZone}). */
export function zoneOrDevice(zone: string | null | undefined): string {
  return isUsableZone(zone) ? zone : deviceZone();
}

/** The `YYYY-MM-DD` an instant falls on in `zone` (`null` or unusable: the device's zone). */
export function zonedDateOf(ms: number, zone: string | null): string {
  const formatter = zone ? zoneDayFormatter(zone) : null;
  if (formatter) {
    let year = 0;
    let month = 0;
    let day = 0;
    for (const part of formatter.formatToParts(new Date(ms))) {
      if (part.type === 'year') year = Number(part.value);
      else if (part.type === 'month') month = Number(part.value);
      else if (part.type === 'day') day = Number(part.value);
    }
    if (year > 0 && month > 0 && day > 0) return ymd(year, month, day);
  }
  const d = new Date(ms);
  return ymd(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/**
 * The UTC calendar date of an instant. No day a person sees uses it: every one, credit-lot
 * "Good through" included (`src/utils/creditExpiry.ts`, Operator ruling 2026-10-15), is on
 * the calendar zone via {@link zonedDateOf} / {@link todayIn}.
 */
export function utcDateOf(ms: number): string {
  const d = new Date(ms);
  return ymd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** Today's `YYYY-MM-DD` in `zone` (`null`: the device). `nowMs` is injectable for tests. */
export function todayIn(zone: string | null, nowMs: number = Date.now()): string {
  return zonedDateOf(nowMs, zone);
}

/**
 * `yyyyMmDd` moved by `n` calendar days (negative goes back), on plain dates: no zone and no
 * DST in play. A value that is not `YYYY-MM-DD` comes back unchanged.
 */
export function addDays(yyyyMmDd: string, n: number): string {
  const m = ISO_DATE.exec(yyyyMmDd);
  if (m === null) return yyyyMmDd;
  const d = new Date(utcDayMs(Number(m[1]), Number(m[2]), Number(m[3]) + n));
  if (Number.isNaN(d.getTime())) return yyyyMmDd;
  return ymd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** Whole calendar days from `from` to `to` (`to` later is positive), or null when either is not a date. */
export function daysBetween(from: string, to: string): number | null {
  const a = ISO_DATE.exec(from);
  const b = ISO_DATE.exec(to);
  if (a === null || b === null) return null;
  const aMs = utcDayMs(Number(a[1]), Number(a[2]), Number(a[3]));
  const bMs = utcDayMs(Number(b[1]), Number(b[2]), Number(b[3]));
  return Math.round((bMs - aMs) / DAY_MS);
}

/**
 * The "last `days` days" window ending today in `zone`: `{ from: today - days, to: today }`,
 * BOTH ends inclusive, so it spans `days + 1` calendar days (the 30-day defaults have always
 * been today-30 through today).
 */
export function rangeBounds(
  zone: string | null,
  days: number,
  nowMs: number = Date.now(),
): { from: string; to: string } {
  const to = todayIn(zone, nowMs);
  return { from: addDays(to, -days), to };
}

/**
 * The instant (ms) a `YYYY-MM-DD` starts in `zone` (`null`: the device), or null when not a date.
 *
 * A usable zone is searched, not computed: the start of a day is the FIRST instant whose
 * local date in the zone is that day (DNKADM-0442 U4). A wall-clock conversion of 00:00 is
 * wrong where the zone changes clocks AT midnight (America/Santiago 2026-09-06 and
 * America/Havana 2026-03-08 skip 00:00 entirely, so "00:00" does not exist and the
 * conversion lands an hour early, on the previous day). The local date never decreases as
 * time moves forward, so a binary search over the minutes of a window that must contain
 * local midnight (every real offset is within UTC-12..UTC+14) finds that first instant
 * whatever the offset change was. On a repeated midnight (clocks fall back) it is the first
 * occurrence. The zone is an explicit argument; a caller with none passes {@link deviceZone}.
 */
function dayStartMs(yyyyMmDd: string, zone: string): number | null {
  const m = ISO_DATE.exec(yyyyMmDd);
  if (m === null || !isUsableZone(zone)) return null;
  const utcMidnight = utcDayMs(Number(m[1]), Number(m[2]), Number(m[3]));
  if (Number.isNaN(utcMidnight)) return null;
  const MINUTE_MS = 60 * 1000;
  // Fast path (two formatter reads instead of ~11): the wall-clock guess for 00:00 in this
  // zone is the day's start when it is on the date and the minute before it is not. Where
  // midnight is skipped or repeated the guess fails that check and the search below decides.
  const guessIso = wallClockToIso(yyyyMmDd, 0, 0, zone);
  const guess = guessIso === null ? NaN : Date.parse(guessIso);
  if (
    Number.isFinite(guess) &&
    zonedDateOf(guess, zone) === yyyyMmDd &&
    zonedDateOf(guess - MINUTE_MS, zone) !== yyyyMmDd
  ) {
    return guess;
  }
  // lo: the local date is still before the target; hi: it is on or after it.
  let lo = Math.floor((utcMidnight - 15 * 60 * MINUTE_MS) / MINUTE_MS);
  let hi = Math.floor((utcMidnight + 13 * 60 * MINUTE_MS) / MINUTE_MS);
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    // `zone` is explicit and usable (checked above): nothing here reads the device zone.
    if (zonedDateOf(mid * MINUTE_MS, zone) >= yyyyMmDd) hi = mid;
    else lo = mid;
  }
  return hi * MINUTE_MS;
}

/** The ISO instant (UTC `Z` form) a day starts in `zone`, or null when not a date. */
export function dayStartIso(yyyyMmDd: string, zone: string): string | null {
  const ms = dayStartMs(yyyyMmDd, zone);
  return ms === null ? null : new Date(ms).toISOString();
}

/**
 * The ISO instant of a day's LAST millisecond in `zone` (the next day's start minus 1 ms, so
 * an inclusive end bound: 23 h on a spring-forward day, 25 h on a fall-back day), or null.
 */
export function dayEndIso(yyyyMmDd: string, zone: string): string | null {
  if (ISO_DATE.exec(yyyyMmDd) === null) return null;
  const next = dayStartMs(addDays(yyyyMmDd, 1), zone);
  return next === null ? null : new Date(next - 1).toISOString();
}

/**
 * An API calendar date for display, from its digits (`2026-10-01` -> `Oct 1, 2026`): never
 * parsed into a `Date`. Anything that is not exactly `YYYY-MM-DD` is shown as sent; missing
 * is `—`.
 */
export function formatApiDate(yyyyMmDd: string | null | undefined): string {
  if (yyyyMmDd === null || yyyyMmDd === undefined || yyyyMmDd.trim() === '') return '—';
  if (ISO_DATE.exec(yyyyMmDd) === null) return yyyyMmDd;
  return shortDateLabel(yyyyMmDd) ?? yyyyMmDd;
}
